import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, CalendarCheck, ArrowUpRight } from "lucide-react";

const SESSION_KEY = "xhr_exit_shown";

function useExitIntent() {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return undefined;
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !sessionStorage.getItem(SESSION_KEY)) {
        sessionStorage.setItem(SESSION_KEY, "1");
        setTriggered(true);
      }
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [setTriggered]);

  return [triggered, setTriggered];
}

function PopupCard({ onClose, onCta }) {
  return (
    <div className="relative max-w-md w-full rounded-lg border border-border bg-card p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
      <button
        data-testid="exit-popup-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-[#2D7E68]/20 text-[#3fae8f] mb-5">
        <CalendarCheck className="h-6 w-6" />
      </div>
      <p className="overline mb-2">Before you go</p>
      <h3 className="text-2xl font-display font-bold text-foreground">
        Get a free hiring strategy session
      </h3>
      <p className="mt-3 text-sm text-white/65 leading-relaxed">
        Talk to our workforce experts about your open roles. No commitment —
        just a 30-minute conversation on how to hire faster and smarter.
      </p>
      <button
        data-testid="exit-popup-cta"
        onClick={onCta}
        className="mt-6 w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-md text-sm font-semibold bg-[#E45E3B] text-white hover:bg-[#c84d2e] transition-colors"
      >
        Book My Free Session
        <ArrowUpRight className="h-4 w-4" />
      </button>
      <button
        data-testid="exit-popup-dismiss"
        onClick={onClose}
        className="mt-3 w-full text-xs text-white/45 hover:text-white/70 transition-colors"
      >
        No thanks, I&apos;m just browsing
      </button>
    </div>
  );
}

export default function ExitIntentPopup() {
  const [open, setOpen] = useExitIntent();
  const navigate = useNavigate();
  const location = useLocation();

  if (!open || location.pathname === "/contact" || location.pathname.startsWith("/admin")) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <PopupCard
        onClose={() => setOpen(false)}
        onCta={() => {
          setOpen(false);
          navigate("/contact");
        }}
      />
    </div>
  );
}
